<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { warningAutoplayPrevented } from '@/core'

// Define Props ----------------------------------
export interface Props {
  playlist: string[]
  autoplay?: boolean
  loop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: true,
  loop: true,
})

// Define Emits ----------------------------------
const emit = defineEmits<{
  played: [{ track: number; replay: boolean }]
  paused: [{ track: number }]
  ended: [{ track: number }]
}>()

// State -----------------------------------------
const { playlist, autoplay, loop } = props
const audio = ref<HTMLAudioElement>()
const trackIndex = ref(0)

// Getters ---------------------------------------

// Methods ---------------------------------------
function play(replay = false) {
  if (audio.value) {
    if (replay) {
      audio.value.currentTime = 0
    }
    audio.value.play()
    emit('played', { track: trackIndex.value, replay })
  }
}

function pause() {
  audio.value?.pause()
  emit('paused', { track: trackIndex.value })
}

function select(track = 0, startPlay = false) {
  trackIndex.value = track < 0 || track >= playlist.length ? 0 : track
  if (audio.value) {
    audio.value.src = playlist[trackIndex.value]
    if (startPlay) {
      setTimeout(() => {
        play()
      }, 100)
    }
  }
}

function next(startPlay = true) {
  if (trackIndex.value + 1 >= playlist.length && !loop) {
    return
  }
  setTimeout(() => {
    select(trackIndex.value + 1, startPlay)
  }, 100)
}

function info() {
  return { track: trackIndex.value, paused: audio.value?.paused ?? false }
}

// Events ----------------------------------------
onMounted(() => {
  select(0)
  if (autoplay && audio.value) {
    if (!warningAutoplayPrevented(audio.value.play())) {
      setTimeout(() => {
        emit('played', { track: trackIndex.value, replay: false })
      }, 500)
    }
  }
})

function onEnded(currentTrack: number) {
  next()
}

// Main Setup ------------------------------------
defineExpose({
  trackIndex,
  play,
  pause,
  next,
  select,
  info,
})

// -----------------------------------------------
</script>

<template>
  <audio ref="audio" controls :data-track="trackIndex" @ended="onEnded(trackIndex)"></audio>
</template>

<style scoped>
/* ------------------------ */
/* ------------------------ */
</style>
